import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { LayoutGroup, motion, useReducedMotion } from 'motion/react';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../ui/button';
import matchesFocusVisible from './matchesFocusVisible';

// How long the open fan-out takes (spec §5: ~0.6s spring, 0.18s lead-in,
// plus the stagger). Cards take no pointer input until it has passed, so a click on a
// collapsing pile cannot land on a card mid-flight.
export const STACK_FLIGHT_MS = 950;
const CLOSE_FLIGHT_MS = 750;

const PILE_SIZE = 3;
// Spec §5 starting values: papers piled on the corkboard, not a casino fan.
// Tune on device; tests do not pin these numbers.
const PILE_POSES = [
  { x: 0, y: 0, rotate: 0 },
  { x: -12, y: 8, rotate: -3 },
  { x: 12, y: 12, rotate: 4 },
];
const LIFTED_POSES = [
  { x: 0, y: 0, rotate: 0 },
  { x: -24, y: 12, rotate: -7 },
  { x: 24, y: 16, rotate: 7 },
];
const SIDE_OFFSET = { left: -40, right: 40, center: 0 };
const HIT_AREA_CLASSES = {
  left: '-top-4 -bottom-8 -left-14 -right-4 after:top-4 after:bottom-8 after:left-14 after:right-4',
  right: '-top-4 -bottom-8 -left-4 -right-14 after:top-4 after:bottom-8 after:left-4 after:right-14',
  center: '-top-4 -bottom-8 -inset-x-14 after:top-4 after:bottom-8 after:inset-x-14',
};
const EASE_OUT = { duration: 0.2, ease: 'easeOut' };
const CARD_SPRING = { type: 'spring', stiffness: 260, damping: 26 };
const STAGGER_S = 0.06;
const NAME_SUFFIX = {
  ready: '',
  loading: '',
  locked: ', locked',
  unavailable: ', temporarily unavailable',
};

const stackName = (label, count, topCardState) => (
  `${label}, ${count} ${count === 1 ? 'case' : 'cases'}${NAME_SUFFIX[topCardState] || ''}`
);

const glowShadow = (rgb) => `0 20px 60px rgba(${rgb}, 0.4)`;

// Presentational: renders the given cards as a closed pile or today's open row.
// Knows nothing about themes, featured cases or routing -- the parent owns all
// data and the open/closed state.
const CaseStack = ({
  id,
  label,
  subtitle,
  cards,
  isOpen,
  onOpen,
  onClose,
  hidden = false,
  topCardState = 'ready',
  side = 'center',
  enterOnMount = false,
  className = '',
  openTestId,
}) => {
  const reduceMotion = useReducedMotion();
  const regionId = `case-stack-${id}-cards`;
  const headingId = `case-stack-${id}-label`;
  const stackButtonRef = useRef(null);
  const backButtonRef = useRef(null);
  const regionRef = useRef(null);
  const topCardRef = useRef(null);
  const wasHiddenRef = useRef(hidden);
  const [hovered, setHovered] = useState(false);
  const [focusVisible, setFocusVisible] = useState(false);
  const lifted = hovered || focusVisible;
  const [landed, setLanded] = useState(true);
  const [prevIsOpen, setPrevIsOpen] = useState(isOpen);
  const [hasToggled, setHasToggled] = useState(false);
  // Sticky by design: only a hidden-to-visible transition needs the delayed un-hide.
  if (hidden) wasHiddenRef.current = true;

  // Adjusting state while rendering (React's "information from previous
  // renders" pattern): the render that flips `isOpen` already knows it is a
  // transition, so the incoming cards get their flight `initial` and the
  // pointer lock on the very first frame. A mount that is already open
  // (arrived via ?stack=) is not a transition: nothing flies, focus stays put.
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    setHasToggled(true);
    setHovered(false);
    setFocusVisible(false);
    setLanded(Boolean(reduceMotion));
  }

  useEffect(() => {
    if (landed) return undefined;
    const timer = setTimeout(() => setLanded(true), isOpen ? STACK_FLIGHT_MS : CLOSE_FLIGHT_MS);
    return () => clearTimeout(timer);
  }, [landed, isOpen]);

  useEffect(() => {
    if (!hasToggled) return;
    if (isOpen) {
      const firstCardAction = regionRef.current?.querySelector('button:not([disabled])');
      (firstCardAction || backButtonRef.current)?.focus({ preventScroll: true });
    } else {
      stackButtonRef.current?.focus({ preventScroll: true });
    }
  }, [isOpen, hasToggled]);

  const pileCards = cards.slice(0, PILE_SIZE);
  const showGlow = lifted && topCardState === 'ready';
  const topGlowRgb = pileCards[0]?.glowRgb;

  // Motion owns styles on layoutId elements, so apply the hover/focus glow
  // after its render to make the current state visible on the first frame.
  useLayoutEffect(() => {
    if (topCardRef.current) {
      topCardRef.current.style.boxShadow = showGlow && topGlowRgb
        ? glowShadow(topGlowRgb)
        : '';
    }
  }, [showGlow, topGlowRgb, isOpen]);

  const heading = (
    <motion.div className="text-center" layoutId="label" layout="position">
      <h2
        id={headingId}
        className="text-3xl font-bold text-gray-900"
      >
        {label}
      </h2>
      <p className="mt-2 text-gray-800">{subtitle}</p>
    </motion.div>
  );

  const closedView = (
    <>
      <div className="mb-6">{heading}</div>
      <div
        className="relative"
        onPointerEnter={(event) => { if (event.pointerType !== 'touch') setHovered(true); }}
        onPointerLeave={() => setHovered(false)}
      >
        <motion.div animate={{ y: lifted ? -6 : 0 }} transition={EASE_OUT}>
          <div id={regionId} className="relative" aria-hidden="true" inert>
            {pileCards.map((card, slot) => (
              <motion.div
                key={card.key}
                ref={slot === 0 ? topCardRef : undefined}
                layoutId={card.key}
                data-stack-slot={slot}
                className={slot === 0 ? 'relative rounded-xl transition-shadow duration-300' : 'absolute inset-0 rounded-xl shadow-xl'}
                style={{
                  zIndex: PILE_SIZE - slot,
                }}
                initial={hasToggled ? { x: 0, y: 0, rotate: 0 } : false}
                animate={lifted ? LIFTED_POSES[slot] : PILE_POSES[slot]}
                transition={{
                  ...EASE_OUT,
                  // Close: cards return right to left.
                  layout: { ...CARD_SPRING, delay: (pileCards.length - 1 - slot) * STAGGER_S },
                }}
              >
                {card.node}
              </motion.div>
            ))}
          </div>
        </motion.div>
        <button
          ref={stackButtonRef}
          type="button"
          data-testid={`case-stack-${id}`}
          aria-label={stackName(label, cards.length, topCardState)}
          aria-expanded="false"
          aria-controls={regionId}
          className={`absolute ${HIT_AREA_CLASSES[side]} z-30 cursor-pointer rounded-xl bg-transparent after:absolute after:rounded-xl after:pointer-events-none after:transition-transform after:duration-200 after:ease-out motion-reduce:after:transition-none focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring ${lifted ? 'after:-translate-y-1.5' : ''}`}
          onClick={onOpen}
          onFocus={(event) => setFocusVisible(matchesFocusVisible(event.currentTarget))}
          onBlur={() => setFocusVisible(false)}
        />
      </div>
    </>
  );

  const openView = (
    <section data-testid={openTestId} aria-labelledby={headingId}>
      <div className="relative mb-6 flex items-center justify-center">
        <motion.div
          className="absolute left-0"
          initial={hasToggled ? { opacity: 0 } : false}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2, delay: reduceMotion ? 0 : 0.4 }}
          style={landed ? undefined : { pointerEvents: 'none' }}
        >
          <Button
            ref={backButtonRef}
            type="button"
            variant="outline"
            className="min-h-touch bg-white/80 hover:bg-white text-gray-800 hover:text-gray-800 border-gray-600 text-sm md:text-base"
            onClick={onClose}
            tabIndex={landed ? undefined : -1}
          >
            <ChevronLeft className="w-4 h-4 mr-1 md:mr-2" aria-hidden="true" />
            Back
          </Button>
        </motion.div>
        {heading}
      </div>
      <div
        id={regionId}
        ref={regionRef}
        className="grid grid-cols-1 md:grid-cols-3 gap-8 w-full"
        style={landed ? undefined : { pointerEvents: 'none' }}
      >
        {cards.map((card, index) => (
          <motion.div
            key={card.key}
            layoutId={card.key}
            data-stack-slot={index}
            initial={
              hasToggled
                ? reduceMotion
                  ? { opacity: 0 }
                  : { rotate: PILE_POSES[Math.min(index, PILE_SIZE - 1)].rotate }
                : false
            }
            animate={{ opacity: 1, rotate: 0 }}
            transition={{
              opacity: { duration: 0.15 },
              rotate: CARD_SPRING,
              // Open: cards travel left to right.
              layout: { ...CARD_SPRING, delay: 0.18 + index * STAGGER_S },
            }}
          >
            {card.node}
          </motion.div>
        ))}
      </div>
    </section>
  );

  return (
    <motion.div
      className={`${className} ${!landed ? 'relative z-10' : ''}`}
      data-case-stack={id}
      initial={enterOnMount ? { opacity: 0, y: 12 } : false}
      animate={
        hidden
          ? { opacity: 0, x: SIDE_OFFSET[side] ?? 0, y: 0, transitionEnd: { visibility: 'hidden' } }
          : { opacity: 1, x: 0, y: 0, visibility: 'visible' }
      }
      transition={hidden ? EASE_OUT : { ...EASE_OUT, delay: wasHiddenRef.current && !isOpen ? 0.3 : 0 }}
    >
      <div
        data-testid={`case-stack-${id}-body`}
        inert={hidden}
        aria-hidden={hidden ? 'true' : undefined}
        style={hidden ? { pointerEvents: 'none' } : undefined}
      >
        <LayoutGroup id={`case-stack-${id}`}>
          {isOpen ? openView : closedView}
        </LayoutGroup>
      </div>
    </motion.div>
  );
};

export default CaseStack;
